import { useEffect, useMemo, useState, type FormEvent } from "react";
import { HelpCircle, Plus, Trash2 } from "lucide-react";
import { Modal } from "../../components/Modal";
import { ErrorNotice } from "../../components/Feedback";
import type { JsonRecord, WorkContext } from "../../types";
import type { ConcreteCatalogs } from "./executionService";
import type { ExecutionTypeConfig } from "./executionTypes";
import { ExecutionCivilDateInput } from "./ExecutionCivilDateInput";
import {
  commercialBarSizes,
  commercialSizeLabel,
  feetToMm,
  mmToPlateFeet,
  plateLengthsForWidth,
  platePlanLabel,
  plateThicknesses,
  uniquePlateWidths,
  type PlatePlanDisplayUnit,
  type SteelLengthDisplayUnit,
} from "./structuralSteelCatalogs";

const rowId = (row: JsonRecord) => String(row.id || row.frente_id || row.subfrente_id || "");
const rowName = (row: JsonRecord) => String(
  row.frente
  || row.subfrente
  || row.nombre
  || row.codigo
  || row.label
  || "Sin nombre",
);

export function ExecutionFormDialog({
  config,
  record,
  fronts,
  subfronts,
  busy,
  error,
  context,
  concreteCatalogs,
  onClose,
  onSave,
}: {
  config: ExecutionTypeConfig;
  record?: JsonRecord;
  fronts: JsonRecord[];
  subfronts: JsonRecord[];
  busy: boolean;
  error: string;
  context: WorkContext;
  concreteCatalogs?: ConcreteCatalogs;
  onClose: () => void;
  onSave: (values: JsonRecord) => void;
}) {
  const [values, setValues] = useState<JsonRecord>(() => Object.fromEntries(
    config.fields.map((field) => [
      field.key,
      record?.[field.key] ?? (field.type === "boolean" ? false : ""),
    ]),
  ));

  const availableSubfronts = useMemo(
    () => subfronts.filter(
      (row) => !values.frente_id || String(row.frente_id || "") === String(values.frente_id),
    ),
    [subfronts, values.frente_id],
  );

  if (config.key === "concreto" && concreteCatalogs) return <ConcreteFormDialog
    record={record} fronts={fronts} subfronts={subfronts} busy={busy} error={error}
    context={context} catalogs={concreteCatalogs} onClose={onClose} onSave={onSave}
  />;
  if (config.key === "terracerias" && concreteCatalogs) return <EarthworksFormDialog
    record={record} fronts={fronts} subfronts={subfronts} structures={concreteCatalogs.structures}
    busy={busy} error={error} onClose={onClose} onSave={onSave}
  />;
  if (config.key === "acero_refuerzo" && concreteCatalogs) return <ReinforcementSteelFormDialog
    record={record} fronts={fronts} subfronts={subfronts} specifications={concreteCatalogs.reinforcementSpecifications}
    busy={busy} error={error} onClose={onClose} onSave={onSave}
  />;
  if (config.key === "acero_estructural" && concreteCatalogs) return <StructuralSteelFormDialog
    record={record} fronts={fronts} subfronts={subfronts} specifications={concreteCatalogs.steelSpecifications}
    busy={busy} error={error} onClose={onClose} onSave={onSave}
  />;

  function submit(event: FormEvent) {
    event.preventDefault();
    const payload = Object.fromEntries(
      config.fields
        .filter((field) => !field.readOnly)
        .map((field) => [
          field.key,
          values[field.key] === ""
            ? null
            : field.type === "number"
              ? Number(values[field.key])
              : values[field.key],
        ]),
    );
    onSave(record?.id ? { id: record.id, ...payload } : payload);
  }

  return <Modal
    title={record ? `Editar registro de ${config.label}` : `Nuevo registro de ${config.label}`}
    className="execution-modal"
    onClose={onClose}
  >
    <form className="execution-form" onSubmit={submit}>
      {error && <ErrorNotice message={error} />}
      <div className="execution-form-grid">
        {config.fields.map((field) => <label key={field.key}>
          {field.label}
          {field.type === "front"
            ? <select
                required={field.required}
                value={String(values[field.key] || "")}
                onChange={(event) => setValues((current) => ({
                  ...current,
                  frente_id: event.target.value,
                  subfrente_id: "",
                }))}
              >
                <option value="">Selecciona…</option>
                {fronts.map((row) => <option key={rowId(row)} value={rowId(row)}>{rowName(row)}</option>)}
              </select>
            : field.type === "subfront"
              ? <select
                  value={String(values[field.key] || "")}
                  disabled={!values.frente_id}
                  onChange={(event) => setValues((current) => ({
                    ...current,
                    [field.key]: event.target.value,
                  }))}
                >
                  <option value="">Sin subfrente</option>
                  {availableSubfronts.map((row) => <option key={rowId(row)} value={rowId(row)}>{rowName(row)}</option>)}
                </select>
              : field.type === "select"
                ? <select
                    required={field.required}
                    value={String(values[field.key] || "")}
                    onChange={(event) => setValues((current) => ({
                      ...current,
                      [field.key]: event.target.value,
                    }))}
                  >
                    <option value="">Selecciona…</option>
                    {(field.options ?? []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                : field.type === "boolean"
                  ? <input
                      type="checkbox"
                      checked={Boolean(values[field.key])}
                      disabled={field.readOnly}
                      onChange={(event) => setValues((current) => ({
                        ...current,
                        [field.key]: event.target.checked,
                      }))}
                    />
                  : <input
                      type={field.type || "text"}
                      step={field.type === "number" ? "any" : undefined}
                      required={field.required}
                      readOnly={field.readOnly}
                      value={String(values[field.key] ?? "")}
                      onChange={(event) => setValues((current) => ({
                        ...current,
                        [field.key]: event.target.value,
                      }))}
                    />}
        </label>)}
      </div>
      <footer>
        <button type="button" className="button ghost" onClick={onClose}>Cancelar</button>
        <button className="button primary" disabled={busy}>
          {busy ? "Guardando…" : "Guardar registro"}
        </button>
      </footer>
    </form>
  </Modal>;
}


function ReinforcementSteelFormDialog({ record, fronts, subfronts, specifications, busy, error, onClose, onSave }: {
  record?: JsonRecord;
  fronts: JsonRecord[];
  subfronts: JsonRecord[];
  specifications: JsonRecord[];
  busy: boolean;
  error: string;
  onClose: () => void;
  onSave: (values: JsonRecord) => void;
}) {
  const initial = (key: string) => record?.[key] ?? "";
  const legacyColadas = useMemo(() => {
    if (Array.isArray(record?.coladas)) {
      return (record?.coladas as unknown[]).map((item) => String(
        typeof item === "object" && item !== null && "colada" in item
          ? (item as JsonRecord).colada
          : item,
      )).filter(Boolean);
    }
    const legacy = String(record?.colada || "").trim();
    return legacy ? legacy.split(";").map((item) => item.trim()).filter(Boolean) : [];
  }, [record]);
  const legacyLotes = useMemo(() => {
    if (Array.isArray(record?.lotes)) {
      return (record?.lotes as unknown[]).map((item) => String(
        typeof item === "object" && item !== null && "lote" in item
          ? (item as JsonRecord).lote
          : item,
      )).filter(Boolean);
    }
    const legacy = String(record?.lote || "").trim();
    return legacy ? legacy.split(";").map((item) => item.trim()).filter(Boolean) : [];
  }, [record]);
  const [traceabilityType, setTraceabilityType] = useState(
    String(record?.trazabilidad_tipo || (legacyColadas.length ? "colada" : legacyLotes.length ? "lote" : "")),
  );
  const [coladas, setColadas] = useState<string[]>(legacyColadas.length ? legacyColadas : [""]);
  const [lotes, setLotes] = useState<string[]>(legacyLotes.length ? legacyLotes : [""]);
  const [values, setValues] = useState<JsonRecord>({
    fecha_suministro: initial("fecha_suministro"),
    folio_remision_proveedor: initial("folio_remision_proveedor"),
    folio_nota_recepcion: initial("folio_nota_recepcion"),
    proveedor: initial("proveedor"),
    frente_id: initial("frente_id"),
    subfrente_id: initial("subfrente_id"),
    especificacion_id: initial("especificacion_id"),
    grado: initial("grado"),
    designacion: initial("designacion") || initial("num_designacion"),
    masa: initial("masa") || initial("peso_kg"),
    unidad_masa: initial("unidad_masa") || (initial("peso_kg") ? "kg" : ""),
  });
  const availableSubfronts = useMemo(() => subfronts.filter((row) => !values.frente_id
    || String(row.frente_id || row.frenteId || "") === String(values.frente_id)), [subfronts, values.frente_id]);
  const set = (key: string, value: unknown) => setValues((current) => ({ ...current, [key]: value }));

  function submit(event: FormEvent) {
    event.preventDefault();
    const numericMass = values.masa === "" || values.masa == null ? null : Number(values.masa);
    onSave({
      ...(record?.id ? { id: record.id } : {}),
      fecha_suministro: values.fecha_suministro || null,
      folio_remision_proveedor: values.folio_remision_proveedor || null,
      folio_nota_recepcion: values.folio_nota_recepcion || null,
      proveedor: values.proveedor || null,
      frente_id: values.frente_id || null,
      subfrente_id: values.subfrente_id || null,
      especificacion_id: values.especificacion_id || null,
      grado: values.grado || null,
      designacion: values.designacion || null,
      colada: traceabilityType === "colada"
        ? coladas.map((item) => item.trim()).filter(Boolean).join("; ") || null
        : null,
      lote: traceabilityType === "lote"
        ? lotes.map((item) => item.trim()).filter(Boolean).join("; ") || null
        : null,
      masa: numericMass,
      unidad_masa: values.unidad_masa || null,
    });
  }

  return <Modal title={record ? "Editar suministro de acero de refuerzo" : "Nuevo suministro de acero de refuerzo"} className="execution-modal reinforcement-steel-modal" onClose={onClose}>
    <form className="execution-form" onSubmit={submit}>
      {error && <ErrorNotice message={error} />}
      <div className="reinforcement-steel-form-grid">
        <label className="rebar-fecha">Fecha de suministro<ExecutionCivilDateInput required ariaLabel="Fecha de suministro" value={String(values.fecha_suministro || "")} onChange={(value) => set("fecha_suministro", value)} /></label>
        <label className="rebar-remision">Folio de remisión del proveedor<input value={String(values.folio_remision_proveedor || "")} onChange={(event) => set("folio_remision_proveedor", event.target.value)} /></label>
        <label className="rebar-recepcion">Folio de nota de recepción<input value={String(values.folio_nota_recepcion || "")} onChange={(event) => set("folio_nota_recepcion", event.target.value)} /></label>
        <label className="rebar-proveedor">Proveedor<input value={String(values.proveedor || "")} onChange={(event) => set("proveedor", event.target.value)} /></label>

        <label className="rebar-frente">Frente<select value={String(values.frente_id || "")} onChange={(event) => setValues((current) => ({ ...current, frente_id: event.target.value, subfrente_id: "" }))}><option value="">Sin frente</option>{fronts.map((row) => <option key={rowId(row)} value={rowId(row)}>{rowName(row)}</option>)}</select></label>
        <label className="rebar-subfrente">Subfrente<select disabled={!values.frente_id} value={String(values.subfrente_id || "")} onChange={(event) => set("subfrente_id", event.target.value)}><option value="">Sin subfrente</option>{availableSubfronts.map((row) => <option key={rowId(row)} value={rowId(row)}>{rowName(row)}</option>)}</select></label>

        <label className="rebar-especificacion">Especificación<select required disabled={specifications.length === 0} value={String(values.especificacion_id || "")} onChange={(event) => set("especificacion_id", event.target.value)}><option value="">{specifications.length ? "Selecciona…" : "Sin especificaciones asignadas"}</option>{specifications.map((row) => { const id = catalogId(row, "especificacion_id", "id"); return <option key={id} value={id}>{String(row.label || row.codigo || "Sin etiqueta")}</option>; })}</select></label>
        <label className="rebar-grado">Grado<input required value={String(values.grado || "")} onChange={(event) => set("grado", event.target.value)} /></label>
        <label className="rebar-designacion">Designación<select required value={String(values.designacion || "")} onChange={(event) => set("designacion", event.target.value)}><option value="">Selecciona…</option>{["2.5","3","4","5","6","7","8","9","10","11","12","14","16","18"].map((item) => <option key={item} value={item}>{item}</option>)}</select></label>

        <div className="rebar-traceability-row">
          <label className="rebar-identification">Identificación<select required value={traceabilityType} onChange={(event) => {
            const next = event.target.value;
            setTraceabilityType(next);
            if (next === "colada") {
              setLotes([""]);
              if (!coladas.length) setColadas([""]);
            } else if (next === "lote") {
              setColadas([""]);
              if (!lotes.length) setLotes([""]);
            }
          }}><option value="">Selecciona…</option><option value="colada">Colada</option><option value="lote">Lote</option></select></label>
          <label className="rebar-mass">Masa<input required type="number" min="0" step="any" value={String(values.masa || "")} onChange={(event) => set("masa", event.target.value)} /></label>
          <label className="rebar-mass-unit">Unidad<select required value={String(values.unidad_masa || "")} onChange={(event) => set("unidad_masa", event.target.value)}><option value="">Selecciona…</option><option value="kg">kg</option><option value="t">t</option></select></label>

          {traceabilityType === "colada" && <div className="rebar-identifiers">
            <span className="rebar-identifiers-label">Coladas</span>
            <div className="rebar-identifiers-list">
              {coladas.map((value, index) => <div className="rebar-identifier-row" key={index}>
                <input required aria-label={`Colada ${index + 1}`} value={value} onChange={(event) => setColadas((current) => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))} />
                {coladas.length > 1 && <button type="button" className="icon-button" aria-label={`Eliminar colada ${index + 1}`} title="Eliminar colada" onClick={() => setColadas((current) => current.filter((_, itemIndex) => itemIndex !== index))}><Trash2 size={16} /></button>}
              </div>)}
            </div>
            <button type="button" className="button ghost small rebar-add-identifier" onClick={() => setColadas((current) => [...current, ""])}><Plus size={15} /> Agregar colada</button>
          </div>}

          {traceabilityType === "lote" && <div className="rebar-identifiers">
            <span className="rebar-identifiers-label">Lotes</span>
            <div className="rebar-identifiers-list">
              {lotes.map((value, index) => <div className="rebar-identifier-row" key={index}>
                <input required aria-label={`Lote ${index + 1}`} value={value} onChange={(event) => setLotes((current) => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))} />
                {lotes.length > 1 && <button type="button" className="icon-button" aria-label={`Eliminar lote ${index + 1}`} title="Eliminar lote" onClick={() => setLotes((current) => current.filter((_, itemIndex) => itemIndex !== index))}><Trash2 size={16} /></button>}
              </div>)}
            </div>
            <button type="button" className="button ghost small rebar-add-identifier" onClick={() => setLotes((current) => [...current, ""])}><Plus size={15} /> Agregar lote</button>
          </div>}
        </div>
      </div>
      <footer><button type="button" className="button ghost" onClick={onClose}>Cancelar</button><button className="button primary" disabled={busy}>{busy ? "Guardando…" : "Guardar suministro"}</button></footer>
    </form>
  </Modal>;
}

const steelProducts = ["Barra", "Perfil", "Placa", "Tablaestaca", "Otro"] as const;
const steelSections = ["Circular", "Cuadrada", "Hexagonal"] as const;
const steelDimensionKeys = ["perfil", "seccion_transversal", "ancho_mm", "largo_mm", "espesor_mm", "diametro_mm", "lado_mm"] as const;

function StructuralSteelFormDialog({ record, fronts, subfronts, specifications, busy, error, onClose, onSave }: {
  record?: JsonRecord;
  fronts: JsonRecord[];
  subfronts: JsonRecord[];
  specifications: JsonRecord[];
  busy: boolean;
  error: string;
  onClose: () => void;
  onSave: (values: JsonRecord) => void;
}) {
  const initial = (key: string) => record?.[key] ?? "";
  const legacyColadas = useMemo(() => {
    if (Array.isArray(record?.coladas)) {
      return (record?.coladas as unknown[]).map((item) => String(
        typeof item === "object" && item !== null && "colada" in item
          ? (item as JsonRecord).colada
          : item,
      )).filter(Boolean);
    }
    const legacy = String(record?.colada || "").trim();
    return legacy ? legacy.split(";").map((item) => item.trim()).filter(Boolean) : [];
  }, [record]);
  const legacyLotes = useMemo(() => {
    if (Array.isArray(record?.lotes)) {
      return (record?.lotes as unknown[]).map((item) => String(
        typeof item === "object" && item !== null && "lote" in item
          ? (item as JsonRecord).lote
          : item,
      )).filter(Boolean);
    }
    const legacy = String(record?.lote || "").trim();
    return legacy ? legacy.split(";").map((item) => item.trim()).filter(Boolean) : [];
  }, [record]);
  const initialTraceability = String(record?.trazabilidad_tipo || (legacyColadas.length ? "colada" : legacyLotes.length ? "lote" : ""));
  const [traceabilityType, setTraceabilityType] = useState(initialTraceability);
  const [coladas, setColadas] = useState<string[]>(legacyColadas.length ? legacyColadas : [""]);
  const [lotes, setLotes] = useState<string[]>(legacyLotes.length ? legacyLotes : [""]);
  const [values, setValues] = useState<JsonRecord>({
    fecha_suministro: initial("fecha_suministro"), folio_remision_proveedor: initial("folio_remision_proveedor"),
    folio_nota_recepcion: initial("folio_nota_recepcion"), frente_id: initial("frente_id"), subfrente_id: initial("subfrente_id"),
    producto: initial("producto"), especificacion_id: initial("especificacion_id"), grado: initial("grado"), perfil: initial("perfil"),
    seccion_transversal: initial("seccion_transversal"), ancho_mm: initial("ancho_mm"), largo_mm: initial("largo_mm"),
    espesor_mm: initial("espesor_mm"), diametro_mm: initial("diametro_mm"), lado_mm: initial("lado_mm"),
    cantidad: initial("cantidad"), unidad_cantidad: initial("unidad_cantidad"), proveedor: initial("proveedor"),
    comentarios: initial("comentarios"),
  });
  const product = String(values.producto || "");
  const section = String(values.seccion_transversal || "");
  const [barDimensionUnit, setBarDimensionUnit] = useState<SteelLengthDisplayUnit>("mm");
  const [plateThicknessUnit, setPlateThicknessUnit] = useState<SteelLengthDisplayUnit>("mm");
  const [platePlanUnit, setPlatePlanUnit] = useState<PlatePlanDisplayUnit>("ft");
  const initialPlateWidthFt = mmToPlateFeet(initial("ancho_mm"));
  const [plateWidthFt, setPlateWidthFt] = useState<number | null>(initialPlateWidthFt);
  const plateLengthOptions = useMemo(() => plateLengthsForWidth(plateWidthFt), [plateWidthFt]);
  const availableSubfronts = useMemo(() => subfronts.filter((row) => !values.frente_id
    || String(row.frente_id || row.frenteId || "") === String(values.frente_id)), [subfronts, values.frente_id]);
  const set = (key: string, value: unknown) => setValues((current) => ({ ...current, [key]: value }));
  const input = (label: string, key: string, options: { type?: string; required?: boolean; min?: string } = {}) => <label className={`steel-${key}`}>{label}<input type={options.type || "text"} required={options.required} min={options.min} step={options.type === "number" ? "any" : undefined} value={String(values[key] ?? "")} onChange={(event) => set(key, event.target.value)} /></label>;

  function changeProduct(next: string) {
    setValues((current) => {
      const result: JsonRecord = { ...current, producto: next };
      for (const key of steelDimensionKeys) result[key] = "";
      return result;
    });
    setPlateWidthFt(null);
  }
  function changeSection(next: string) {
    setValues((current) => ({ ...current, seccion_transversal: next, diametro_mm: "", lado_mm: "" }));
  }
  function submit(event: FormEvent) {
    event.preventDefault();
    const numeric = (key: string) => values[key] === "" || values[key] == null ? null : Number(values[key]);
    onSave({
      ...(record?.id ? { id: record.id } : {}),
      fecha_suministro: values.fecha_suministro || null, folio_remision_proveedor: values.folio_remision_proveedor || null,
      folio_nota_recepcion: values.folio_nota_recepcion || null, frente_id: values.frente_id || null,
      subfrente_id: values.subfrente_id || null, producto: values.producto || null, especificacion_id: values.especificacion_id || null,
      grado: values.grado || null, perfil: product === "Perfil" ? values.perfil || null : null,
      seccion_transversal: product === "Barra" ? values.seccion_transversal || null : null,
      ancho_mm: product === "Placa" ? numeric("ancho_mm") : null, largo_mm: product === "Placa" ? numeric("largo_mm") : null,
      espesor_mm: product === "Placa" ? numeric("espesor_mm") : null,
      diametro_mm: product === "Barra" && ["Circular", "Hexagonal"].includes(section) ? numeric("diametro_mm") : null,
      lado_mm: product === "Barra" && section === "Cuadrada" ? numeric("lado_mm") : null,
      colada: traceabilityType === "colada"
        ? coladas.map((item) => item.trim()).filter(Boolean).join("; ") || null
        : null,
      lote: traceabilityType === "lote"
        ? lotes.map((item) => item.trim()).filter(Boolean).join("; ") || null
        : null,
      cantidad: numeric("cantidad"),
      unidad_cantidad: values.unidad_cantidad || null, proveedor: values.proveedor || null, comentarios: values.comentarios || null,
    });
  }

  return <Modal title={record ? "Editar suministro de acero estructural" : "Nuevo suministro de acero estructural"} className="execution-modal structural-steel-modal" onClose={onClose}>
    <form className="execution-form" onSubmit={submit}>{error && <ErrorNotice message={error}/>}<div className="structural-steel-form-grid">
      <label className="steel-fecha_suministro">Fecha de suministro<ExecutionCivilDateInput required ariaLabel="Fecha de suministro" value={String(values.fecha_suministro || "")} onChange={(value) => set("fecha_suministro", value)} /></label>
      {input("Folio de remisión del proveedor", "folio_remision_proveedor")}
      {input("Folio de nota de recepción", "folio_nota_recepcion")}
      {input("Proveedor", "proveedor")}
      <label className="steel-frente_id">Frente<select required value={String(values.frente_id || "")} onChange={(event) => setValues((current) => ({ ...current, frente_id: event.target.value, subfrente_id: "" }))}><option value="">Selecciona…</option>{fronts.map((row) => <option key={rowId(row)} value={rowId(row)}>{rowName(row)}</option>)}</select></label>
      <label className="steel-subfrente_id">Subfrente<select disabled={!values.frente_id} value={String(values.subfrente_id || "")} onChange={(event) => set("subfrente_id", event.target.value)}><option value="">Sin subfrente</option>{availableSubfronts.map((row) => <option key={rowId(row)} value={rowId(row)}>{rowName(row)}</option>)}</select></label>
      <label className="steel-producto">Producto<select required value={product} onChange={(event) => changeProduct(event.target.value)}><option value="">Selecciona…</option>{steelProducts.map((item) => <option key={item}>{item}</option>)}</select></label>
      <label className="steel-especificacion_id">Especificación<select disabled={specifications.length === 0} value={String(values.especificacion_id || "")} onChange={(event) => set("especificacion_id", event.target.value)}><option value="">{specifications.length ? "Sin especificación" : "Sin especificaciones asignadas"}</option>{specifications.map((row) => { const id = catalogId(row, "especificacion_id", "id"); return <option key={id} value={id}>{String(row.label || "Sin etiqueta")}</option>; })}</select></label>
      {input("Grado", "grado")}
      {product === "Perfil" && input("Designación del perfil", "perfil", { required: true })}
      {product === "Barra" && <label className="steel-seccion_transversal">Sección<select required value={section} onChange={(event) => changeSection(event.target.value)}><option value="">Selecciona…</option>{steelSections.map((item) => <option key={item}>{item}</option>)}</select></label>}
      {product === "Barra" && section && <label className="steel-bar-unit">Unidad de dimensión<select value={barDimensionUnit} onChange={(event) => setBarDimensionUnit(event.target.value as SteelLengthDisplayUnit)}><option value="mm">Milímetros</option><option value="in">Pulgadas</option></select></label>}
      {product === "Barra" && ["Circular", "Hexagonal"].includes(section) && <label className="steel-diametro_mm">Diámetro<select required value={String(values.diametro_mm ?? "")} onChange={(event) => set("diametro_mm", event.target.value)}><option value="">Selecciona…</option>{commercialBarSizes.map((size) => <option key={size.mm} value={size.mm}>{commercialSizeLabel(size, barDimensionUnit)}</option>)}</select></label>}
      {product === "Barra" && section === "Cuadrada" && <label className="steel-lado_mm">Lado<select required value={String(values.lado_mm ?? "")} onChange={(event) => set("lado_mm", event.target.value)}><option value="">Selecciona…</option>{commercialBarSizes.map((size) => <option key={size.mm} value={size.mm}>{commercialSizeLabel(size, barDimensionUnit)}</option>)}</select></label>}
      {product === "Placa" && <div className="steel-plate-dimensions">
        <label className="steel-espesor_mm">Espesor<select required value={String(values.espesor_mm ?? "")} onChange={(event) => set("espesor_mm", event.target.value)}><option value="">Selecciona…</option>{plateThicknesses.map((size) => <option key={size.mm} value={size.mm}>{commercialSizeLabel(size, plateThicknessUnit)}</option>)}</select></label>
        <label className="steel-thickness-unit">Unidad de espesor<select value={plateThicknessUnit} onChange={(event) => setPlateThicknessUnit(event.target.value as SteelLengthDisplayUnit)}><option value="mm">Milímetros</option><option value="in">Pulgadas</option></select></label>
        <label className="steel-ancho_mm">Ancho<select required value={plateWidthFt == null ? "" : String(plateWidthFt)} onChange={(event) => {
          const widthFt = event.target.value ? Number(event.target.value) : null;
          setPlateWidthFt(widthFt);
          setValues((current) => ({ ...current, ancho_mm: widthFt == null ? "" : feetToMm(widthFt), largo_mm: "" }));
        }}><option value="">Selecciona…</option>{uniquePlateWidths().map((feet) => <option key={feet} value={feet}>{platePlanLabel(feet, platePlanUnit)}</option>)}</select></label>
        <label className="steel-largo_mm">Largo<select required disabled={plateWidthFt == null} value={String(values.largo_mm ?? "")} onChange={(event) => set("largo_mm", event.target.value)}><option value="">Selecciona…</option>{plateLengthOptions.map((feet) => {
          const mm = feetToMm(feet);
          return <option key={feet} value={mm}>{platePlanLabel(feet, platePlanUnit)}</option>;
        })}</select></label>
        <label className="steel-plan-unit">Unidad de ancho / largo<select value={platePlanUnit} onChange={(event) => setPlatePlanUnit(event.target.value as PlatePlanDisplayUnit)}><option value="ft">Pies</option><option value="in">Pulgadas</option></select></label>
      </div>}
      <div className="steel-traceability">
        <div className="steel-traceability-main">
          <label className="steel-traceability-type">Identificación<select required value={traceabilityType} onChange={(event) => {
            const next = event.target.value;
            setTraceabilityType(next);
            if (next === "colada") {
              setLotes([""]);
              if (!coladas.length) setColadas([""]);
            } else if (next === "lote") {
              setColadas([""]);
              if (!lotes.length) setLotes([""]);
            }
          }}><option value="">Selecciona…</option><option value="colada">Colada</option><option value="lote">Lote</option></select></label>
          {input("Cantidad", "cantidad", { type: "number", min: "0" })}
          <label className="steel-unidad_cantidad">Unidad de cantidad<select value={String(values.unidad_cantidad || "")} onChange={(event) => set("unidad_cantidad", event.target.value)}><option value="">Selecciona…</option><option value="kg">Masa (kg)</option><option value="t">Toneladas (t)</option><option value="pieza">Número de piezas</option></select></label>
        </div>
        {traceabilityType === "colada" && <div className="steel-identifiers">
          <span className="steel-identifiers-label">Coladas</span>
          <div className="steel-identifiers-list">
            {coladas.map((value, index) => <div className="steel-identifier-row" key={index}>
              <input required aria-label={`Colada ${index + 1}`} value={value} onChange={(event) => setColadas((current) => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))} />
              {coladas.length > 1 && <button type="button" className="icon-button" aria-label={`Eliminar colada ${index + 1}`} title="Eliminar colada" onClick={() => setColadas((current) => current.filter((_, itemIndex) => itemIndex !== index))}><Trash2 size={16} /></button>}
            </div>)}
          </div>
          <button type="button" className="button ghost small steel-add-identifier" onClick={() => setColadas((current) => [...current, ""])}><Plus size={15} /> Agregar colada</button>
        </div>}
        {traceabilityType === "lote" && <div className="steel-identifiers">
          <span className="steel-identifiers-label">Lotes</span>
          <div className="steel-identifiers-list">
            {lotes.map((value, index) => <div className="steel-identifier-row" key={index}>
              <input required aria-label={`Lote ${index + 1}`} value={value} onChange={(event) => setLotes((current) => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))} />
              {lotes.length > 1 && <button type="button" className="icon-button" aria-label={`Eliminar lote ${index + 1}`} title="Eliminar lote" onClick={() => setLotes((current) => current.filter((_, itemIndex) => itemIndex !== index))}><Trash2 size={16} /></button>}
            </div>)}
          </div>
          <button type="button" className="button ghost small steel-add-identifier" onClick={() => setLotes((current) => [...current, ""])}><Plus size={15} /> Agregar lote</button>
        </div>}
      </div>
      <label className="steel-comentarios">Comentarios<textarea rows={3} value={String(values.comentarios || "")} onChange={(event) => set("comentarios", event.target.value)} /></label>
    </div><footer><button type="button" className="button ghost" onClick={onClose}>Cancelar</button><button className="button primary" disabled={busy}>{busy ? "Guardando…" : "Guardar suministro"}</button></footer></form>
  </Modal>;
}

const catalogId = (row: JsonRecord, ...keys: string[]) => {
  for (const key of keys) if (row[key]) return String(row[key]);
  return "";
};
const catalogText = (row: JsonRecord, ...keys: string[]) => {
  for (const key of keys) if (row[key]) return String(row[key]);
  return "Sin nombre";
};
function ConcreteFormDialog({ record, fronts, subfronts, busy, error, context, catalogs, onClose, onSave }: {
  record?: JsonRecord;
  fronts: JsonRecord[];
  subfronts: JsonRecord[];
  busy: boolean;
  error: string;
  context: WorkContext;
  catalogs: ConcreteCatalogs;
  onClose: () => void;
  onSave: (values: JsonRecord) => void;
}) {
  const admin = String(context.rol).trim().toLocaleLowerCase("es") === "admin";
  const initial = (key: string, fallback: unknown = "") => record?.[key] ?? fallback;
  const [values, setValues] = useState<JsonRecord>({
    fecha: initial("fecha"), frente_id: initial("frente_id"), subfrente_id: initial("subfrente_id"),
    ...(admin ? { area_id: initial("area_id") } : {}), elemento: initial("elemento"),
    ubicacion: initial("ubicacion"), proveedor: initial("proveedor"),
    actividad_material_id: initial("actividad_material_id"), especificacion_id: initial("especificacion_id"),
    resistencia: initial("resistencia"), resistencia_unidad_id: initial("resistencia_unidad_id"),
    volumen: initial("volumen"), cantidad_entregas: initial("cantidad_entregas"), garantia: initial("garantia"),
    muestras_co: initial("muestras_co", 0), ctrl_fresco_co: Boolean(initial("ctrl_fresco_co", false)),
    sup_ext: Boolean(initial("sup_ext", false)), comentarios: initial("comentarios"),
  });
  const set = (key: string, value: unknown) => setValues((current) => ({ ...current, [key]: value }));
  const availableSubfronts = useMemo(() => subfronts.filter((row) =>
    !values.frente_id || String(row.frente_id || "") === String(values.frente_id)), [subfronts, values.frente_id]);
  const specifications = useMemo(() => catalogs.specifications.filter((row) => {
    const materialId = catalogId(row, "actividad_material_id", "material_id");
    return materialId === String(values.actividad_material_id || "");
  }), [catalogs.specifications, values.actividad_material_id]);

  useEffect(() => {
    if (values.especificacion_id && !specifications.some((row) =>
      catalogId(row, "especificacion_id", "id") === String(values.especificacion_id))) {
      set("especificacion_id", "");
    }
  }, [specifications, values.especificacion_id]);

  useEffect(() => {
    if (values.subfrente_id && !availableSubfronts.some((row) =>
      catalogId(row, "id", "subfrente_id") === String(values.subfrente_id))) {
      set("subfrente_id", "");
    }
  }, [availableSubfronts, values.subfrente_id]);

  const select = (label: string, key: string, rows: JsonRecord[], idKeys: string[], text: (row: JsonRecord) => string, required = false, disabled = false) =>
    <label className={`concrete-${key}`}>{label}<select required={required} disabled={disabled} value={String(values[key] ?? "")} onChange={(event) => set(key, event.target.value)}>
      <option value="">Selecciona…</option>{rows.map((row) => { const id = catalogId(row, ...idKeys); return <option key={id} value={id}>{text(row)}</option>; })}
    </select></label>;
  const input = (label: React.ReactNode, key: string, options: { required?: boolean; type?: string; min?: number; step?: string; integer?: boolean } = {}) =>
    <label className={`concrete-${key}`}>{label}<input type={options.type || "text"} required={options.required} min={options.min} step={options.step}
      inputMode={options.integer ? "numeric" : undefined} value={String(values[key] ?? "")}
      onChange={(event) => { const next = event.target.value; if (!options.integer || /^\d*$/.test(next)) set(key, next); }} /></label>;

  function submit(event: FormEvent) {
    event.preventDefault();
    const numeric = (key: string) => values[key] === "" ? null : Number(values[key]);
    onSave({ ...(record?.id ? { id: record.id } : {}), ...values,
      ...(admin ? { area_id: values.area_id } : {}),
      resistencia: numeric("resistencia"), volumen: numeric("volumen"),
      cantidad_entregas: numeric("cantidad_entregas"), garantia: numeric("garantia"),
      muestras_co: numeric("muestras_co"), ctrl_fresco_co: Boolean(values.ctrl_fresco_co), sup_ext: Boolean(values.sup_ext),
    });
  }

  return <Modal title="Registro de colado" className="execution-modal concrete-modal" onClose={onClose}>
    <form className="execution-form" onSubmit={submit}>{error && <ErrorNotice message={error} />}
      <div className="concrete-form-grid">
        {input("Fecha de colado", "fecha", { type: "date", required: true })}
        {select("Tipo de mezcla", "actividad_material_id", catalogs.mixtures, ["id"], (row) => catalogText(row, "actividad_material", "label"), true)}
        {select("Especificación", "especificacion_id", specifications, ["especificacion_id", "id"], (row) => [row.codigo, row.titulo].filter(Boolean).join(" — ") || "Sin nombre", true, !values.actividad_material_id)}
        {select("Frente", "frente_id", fronts, ["id", "frente_id"], (row) => catalogText(row, "frente", "nombre"), true)}
        {select("Subfrente", "subfrente_id", availableSubfronts, ["id", "subfrente_id"], (row) => catalogText(row, "subfrente", "nombre"), false, !values.frente_id)}
        {admin && select("Área", "area_id", catalogs.areas, ["area_id", "id"], (row) => catalogText(row, "area", "nombre", "area_nombre"), true)}
        {input("Elemento", "elemento", { required: true })}{input("Ubicación", "ubicacion")}{input("Proveedor", "proveedor")}
        {input("Volumen (m³)", "volumen", { type: "number", min: 0, step: "any" })}
        {input("Bachas / ollas", "cantidad_entregas", { type: "number", min: 1, step: "1", integer: true })}
        {input("Resistencia", "resistencia", { type: "number", min: 0, step: "any", required: true })}
        {select("Unidad", "resistencia_unidad_id", catalogs.pressureUnits, ["resistencia_unidad_id", "id"], (row) => `${catalogText(row, "nombre", "codigo")}${row.simbolo ? ` (${row.simbolo})` : ""}`, true)}
        {input("Garantía (días)", "garantia", { type: "number", min: 0, step: "1", integer: true })}
        {input(<span className="label-with-help">Muestras del contratista <span className="help-tooltip" tabIndex={0} aria-label="Muestras para evaluar la resistencia"><HelpCircle size={15} /><span role="tooltip">Muestras para evaluar la resistencia</span></span></span>, "muestras_co", { type: "number", min: 0, step: "1", integer: true })}
        <label className="concrete-toggle"><input type="checkbox" checked={Boolean(values.ctrl_fresco_co)} onChange={(event) => set("ctrl_fresco_co", event.target.checked)} />El contratista ejecutó las pruebas en estado fresco</label>
        <label className="concrete-toggle"><input type="checkbox" checked={Boolean(values.sup_ext)} onChange={(event) => set("sup_ext", event.target.checked)} />Trabajos dentro de los alcances de la supervisión externa</label>
        <label className="concrete-comments">Comentarios<textarea rows={3} value={String(values.comentarios ?? "")} onChange={(event) => set("comentarios", event.target.value)} /></label>
      </div><footer><button type="button" className="button ghost" onClick={onClose}>Cancelar</button><button className="button primary" disabled={busy}>{busy ? "Guardando…" : "Guardar registro"}</button></footer>
    </form>
  </Modal>;
}

type EarthworksKind = "Acotado" | "Lineal" | "Puntual";
const earthworksLocationKeys = [
  "cad_inicial", "cad_final", "punto_inicial", "punto_final", "franja",
  "longitud_tramo_drenaje", "ubicacion_puntual", "area",
] as const;

function EarthworksFormDialog({ record, fronts, subfronts, structures, busy, error, onClose, onSave }: {
  record?: JsonRecord;
  fronts: JsonRecord[];
  subfronts: JsonRecord[];
  structures: JsonRecord[];
  busy: boolean;
  error: string;
  onClose: () => void;
  onSave: (values: JsonRecord) => void;
}) {
  const initial = (key: string, fallback: unknown = "") => record?.[key] ?? fallback;
  const [values, setValues] = useState<JsonRecord>({
    tipo_obra: initial("tipo_obra"), actividad_material_id: initial("actividad_material_id"),
    fecha_termino: initial("fecha_termino"), frente_id: initial("frente_id"),
    subfrente_id: initial("subfrente_id"), sup_ext: Boolean(initial("sup_ext", true)),
    capa: initial("capa"), cad_inicial: initial("cad_inicial"), cad_final: initial("cad_final"),
    punto_inicial: initial("punto_inicial"), punto_final: initial("punto_final"), franja: initial("franja"),
    longitud_tramo_drenaje: initial("longitud_tramo_drenaje"), ubicacion_puntual: initial("ubicacion_puntual"),
    area: initial("area"), comentarios: initial("comentarios"),
  });
  const kind = String(values.tipo_obra || "") as EarthworksKind | "";
  const set = (key: string, value: unknown) => setValues((current) => ({ ...current, [key]: value }));
  const availableSubfronts = useMemo(() => subfronts.filter((row) =>
    !values.frente_id || String(row.frente_id || "") === String(values.frente_id)), [subfronts, values.frente_id]);

  function changeKind(next: EarthworksKind | "") {
    setValues((current) => {
      const changed = current.tipo_obra && current.tipo_obra !== next;
      if (changed && !window.confirm("Cambiar el tipo de obra limpiará los campos de ubicación incompatibles. ¿Continuar?")) return current;
      const result: JsonRecord = { ...current, tipo_obra: next };
      if (changed) for (const key of earthworksLocationKeys) delete result[key];
      return result;
    });
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const number = (key: string) => values[key] === "" ? null : Number(values[key]);
    const payload: JsonRecord = {
      ...(record?.id ? { id: record.id } : {}), tipo_obra: kind,
      actividad_material_id: values.actividad_material_id, fecha_termino: values.fecha_termino,
      frente_id: values.frente_id, subfrente_id: values.subfrente_id || null,
      sup_ext: Boolean(values.sup_ext), capa: values.capa, comentarios: values.comentarios || null,
    };
    if (kind === "Lineal") Object.assign(payload, { cad_inicial: number("cad_inicial"), cad_final: number("cad_final"), franja: values.franja || null });
    if (kind === "Acotado") Object.assign(payload, { punto_inicial: values.punto_inicial, punto_final: values.punto_final, franja: values.franja || null, longitud_tramo_drenaje: number("longitud_tramo_drenaje") });
    if (kind === "Puntual") Object.assign(payload, { ubicacion_puntual: values.ubicacion_puntual, area: number("area") });
    onSave(payload);
  }

  return <Modal title={record ? "Editar conformación de terracería" : "Registro de conformación de terracería"} className="execution-modal earthworks-modal" onClose={onClose}>
    <form className="execution-form" onSubmit={submit}>{error && <ErrorNotice message={error} />}
      <div className="earthworks-form-grid">
        <label className="earthworks-kind">Tipo de obra<select required value={kind} onChange={(event) => changeKind(event.target.value as EarthworksKind | "")}><option value="">Selecciona…</option>{["Acotado", "Lineal", "Puntual"].map((item) => <option key={item}>{item}</option>)}</select></label>
        <label className="earthworks-structure">Estructura<select required value={String(values.actividad_material_id || "")} onChange={(event) => set("actividad_material_id", event.target.value)}><option value="">Selecciona…</option>{structures.map((row) => <option key={catalogId(row, "id")} value={catalogId(row, "id")}>{catalogText(row, "actividad_material")}</option>)}</select></label>
        <label className="earthworks-date">Fecha de término<input type="date" required value={String(values.fecha_termino || "")} onChange={(event) => set("fecha_termino", event.target.value)} /></label>
        <label className="earthworks-front">Frente<select required value={String(values.frente_id || "")} onChange={(event) => setValues((current) => ({ ...current, frente_id: event.target.value, subfrente_id: "" }))}><option value="">Selecciona…</option>{fronts.map((row) => <option key={rowId(row)} value={rowId(row)}>{rowName(row)}</option>)}</select></label>
        <label className="earthworks-subfront">Subfrente<select disabled={!values.frente_id} value={String(values.subfrente_id || "")} onChange={(event) => set("subfrente_id", event.target.value)}><option value="">Sin subfrente</option>{availableSubfronts.map((row) => <option key={rowId(row)} value={rowId(row)}>{rowName(row)}</option>)}</select></label>
        <label className="earthworks-toggle"><input type="checkbox" checked={Boolean(values.sup_ext)} onChange={(event) => set("sup_ext", event.target.checked)} />Los trabajos están dentro de los alcances de la Supervisión externa.</label>
        {kind && <div className={`earthworks-location ${kind.toLowerCase()}`}>
          <label className="earthworks-layer">Capa *<input required value={String(values.capa || "")} onChange={(event) => set("capa", event.target.value)} /></label>
          {kind === "Lineal" && <><label>Cadenamiento inicial<input required type="number" step="any" value={String(values.cad_inicial ?? "")} onChange={(event) => set("cad_inicial", event.target.value)} /></label><label>Cadenamiento final<input required type="number" step="any" value={String(values.cad_final ?? "")} onChange={(event) => set("cad_final", event.target.value)} /></label><label>Franja<input value={String(values.franja || "")} onChange={(event) => set("franja", event.target.value)} /></label></>}
          {kind === "Acotado" && <><label>Punto inicial<input required value={String(values.punto_inicial || "")} onChange={(event) => set("punto_inicial", event.target.value)} /></label><label>Punto final<input required value={String(values.punto_final || "")} onChange={(event) => set("punto_final", event.target.value)} /></label><label>Franja<input value={String(values.franja || "")} onChange={(event) => set("franja", event.target.value)} /></label><label className="earthworks-length">Longitud, m<input type="number" min="0" step="any" value={String(values.longitud_tramo_drenaje ?? "")} onChange={(event) => set("longitud_tramo_drenaje", event.target.value)} /></label></>}
          {kind === "Puntual" && <><label className="earthworks-point">Ubicación<input required value={String(values.ubicacion_puntual || "")} onChange={(event) => set("ubicacion_puntual", event.target.value)} /></label><label className="earthworks-area">Área, m²<input required type="number" min="0" step="any" value={String(values.area ?? "")} onChange={(event) => set("area", event.target.value)} /></label></>}
        </div>}
        <label className="earthworks-comments">Comentarios<textarea rows={3} value={String(values.comentarios || "")} onChange={(event) => set("comentarios", event.target.value)} /></label>
      </div><footer><button type="button" className="button ghost" onClick={onClose}>Cancelar</button><button className="button primary" disabled={busy}>{busy ? "Guardando…" : "Guardar registro"}</button></footer>
    </form>
  </Modal>;
}

export function ExecutionDetailDialog({
  config,
  record,
  onClose,
}: {
  config: ExecutionTypeConfig;
  record: JsonRecord;
  onClose: () => void;
}) {
  if (config.key === "acero_refuerzo") {
    const coladas = Array.isArray(record.coladas)
      ? (record.coladas as unknown[]).map((item) => String(
          typeof item === "object" && item !== null && "colada" in item ? (item as JsonRecord).colada : item,
        )).filter(Boolean)
      : String(record.colada || "").split(";").map((item) => item.trim()).filter(Boolean);
    const lotes = Array.isArray(record.lotes)
      ? (record.lotes as unknown[]).map((item) => String(
          typeof item === "object" && item !== null && "lote" in item ? (item as JsonRecord).lote : item,
        )).filter(Boolean)
      : String(record.lote || "").split(";").map((item) => item.trim()).filter(Boolean);

    const fields: Array<{ key: string; label: string; value: string }> = [
      { key: "frente", label: "Frente", value: String(record.frente || "") },
      { key: "subfrente", label: "Subfrente", value: String(record.subfrente || "") },
      ...(coladas.length ? [{ key: "coladas", label: coladas.length === 1 ? "Colada" : "Coladas", value: coladas.join("\n") }] : []),
      ...(lotes.length ? [{ key: "lotes", label: lotes.length === 1 ? "Lote" : "Lotes", value: lotes.join("\n") }] : []),
    ].filter((item) => item.value.trim() !== "");

    return <Modal title="Datos adicionales del suministro" className="execution-modal reinforcement-steel-modal" onClose={onClose}>
      <div className="structural-steel-additional-grid">
        {fields.length
          ? fields.map((field) => <div key={field.key}><span className="field-label">{field.label}</span><div className="steel-additional-value">{field.value}</div></div>)
          : <p className="structural-steel-additional-empty">No hay datos adicionales registrados.</p>}
      </div>
      <footer><button className="button primary" onClick={onClose}>Cerrar</button></footer>
    </Modal>;
  }

  if (config.key === "acero_estructural") {
    const coladas = Array.isArray(record.coladas)
      ? (record.coladas as unknown[]).map((item) => String(
          typeof item === "object" && item !== null && "colada" in item
            ? (item as JsonRecord).colada
            : item,
        )).filter(Boolean)
      : String(record.colada || "").split(";").map((item) => item.trim()).filter(Boolean);
    const lotes = Array.isArray(record.lotes)
      ? (record.lotes as unknown[]).map((item) => String(
          typeof item === "object" && item !== null && "lote" in item
            ? (item as JsonRecord).lote
            : item,
        )).filter(Boolean)
      : String(record.lote || "").split(";").map((item) => item.trim()).filter(Boolean);
    const fields: Array<{ key: string; label: string; value: string }> = [
      { key: "folio_remision_proveedor", label: "Folio de remisión del proveedor", value: String(record.folio_remision_proveedor || "") },
      { key: "folio_nota_recepcion", label: "Folio de nota de recepción", value: String(record.folio_nota_recepcion || "") },
      { key: "frente", label: "Frente", value: String(record.frente || "") },
      { key: "subfrente", label: "Subfrente", value: String(record.subfrente || "") },
      ...(coladas.length ? [{ key: "coladas", label: coladas.length === 1 ? "Colada" : "Coladas", value: coladas.join("\n") }] : []),
      ...(lotes.length ? [{ key: "lotes", label: lotes.length === 1 ? "Lote" : "Lotes", value: lotes.join("\n") }] : []),
      { key: "comentarios", label: "Comentarios", value: String(record.comentarios || "") },
    ].filter((item) => item.value.trim() !== "");

    return <Modal title="Datos adicionales del suministro" className="execution-modal structural-steel-modal" onClose={onClose}>
      <div className="structural-steel-additional-grid">
        {fields.length
          ? fields.map((field) => <div key={field.key} className={field.key === "comentarios" ? "steel-additional-comments" : undefined}>
              <span className="field-label">{field.label}</span>
              <div className="steel-additional-value">{field.value}</div>
            </div>)
          : <p className="structural-steel-additional-empty">No hay datos adicionales registrados.</p>}
      </div>
      <footer><button className="button primary" onClick={onClose}>Cerrar</button></footer>
    </Modal>;
  }
  if (config.key === "concreto") {
    const fields = [
      ["tipo_mezcla_label", "Tipo de mezcla"], ["especificacion", "Especificación"],
      ["proveedor", "Proveedor"], ["estado_revision", "Estado de revisión"],
    ];
    const counts = [
      ["muestras_resistencia_contratista", "Muestras para resistencia del contratista"],
      ["muestras_resistencia_supervision", "Muestras para resistencia de la Supervisión"],
      ["pruebas_fresco_supervision", "Pruebas en estado fresco de la Supervisión"],
    ];
    return <Modal title="Detalles del colado" className="execution-modal concrete-detail-modal" onClose={onClose}>
      <dl className="execution-details concrete-details">
        {fields.map(([key, label]) => <div key={key}><dt>{label}</dt><dd>{record[key] == null || record[key] === "" ? "—" : String(record[key])}</dd></div>)}
        {counts.map(([key, label]) => <div key={key}><dt>{label}</dt><dd>{record[key] == null || record[key] === "" ? "0" : String(record[key])}</dd></div>)}
        <div className="full"><dt>Comentarios</dt><dd>{record.comentarios == null || record.comentarios === "" ? "—" : String(record.comentarios)}</dd></div>
      </dl>
      <footer><button className="button primary" onClick={onClose}>Cerrar</button></footer>
    </Modal>;
  }
  return <Modal
    title={`Detalle de ${config.label}`}
    className="execution-modal"
    onClose={onClose}
  >
    <dl className="execution-details">
      {config.detailFields.map((field) => {
        const value = record[field.key];
        return <div key={field.key}>
          <dt>{field.label}</dt>
          <dd>{value == null || value === ""
            ? "—"
            : typeof value === "object"
              ? JSON.stringify(value)
              : String(value)}</dd>
        </div>;
      })}
    </dl>
    <footer>
      <button className="button primary" onClick={onClose}>Cerrar</button>
    </footer>
  </Modal>;
}
